import React, { useLayoutEffect, useRef } from "react";
import { gsap, ScrollTrigger, SplitText, EASE, prefersReducedMotion } from "./gsap";

// Building blocks for public pages in the "annual report" style.
//
//   <PageOpening section="§ 03" label="About" title="…" intro="…" />
//   <Section number="01" label="The problem" aside={<MarginNote>…</MarginNote>}>…</Section>
//   <Rule />            hairline that draws across when scrolled into view
//   <Figure value="15" unit="years" caption="…" />   ledger-style number
//
// Motion: titles set line by line, rules draw, nothing floats or glows.

// Sets a heading line by line when it scrolls into view (or on mount).
export function useTypeset(ref, { onMount = false } = {}) {
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el || prefersReducedMotion()) return;
        gsap.set(el, { autoAlpha: 0 });
        let split;
        let cancelled = false;
        document.fonts.ready.then(() => {
            if (cancelled || !ref.current) return;
            gsap.set(el, { autoAlpha: 1 });
            split = SplitText.create(el, {
                type: "lines",
                mask: "lines",
                autoSplit: true,
                onSplit: (self) =>
                    gsap.from(self.lines, {
                        yPercent: 105,
                        duration: 1.2,
                        ease: EASE,
                        stagger: 0.08,
                        delay: onMount ? 0.15 : 0,
                        scrollTrigger: onMount ? undefined : { trigger: el, start: "top 95%", once: true },
                    }),
            });
        });
        return () => {
            cancelled = true;
            split?.revert();
        };
    }, [ref, onMount]);
}

export function Rule({ className = "", strong = false }) {
    const ref = useRef(null);
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el || prefersReducedMotion()) return;
        const tween = gsap.fromTo(
            el,
            { scaleX: 0 },
            { scaleX: 1, duration: 1.4, ease: EASE, scrollTrigger: { trigger: el, start: "top 92%", once: true } }
        );
        return () => {
            tween.scrollTrigger?.kill();
            tween.kill();
        };
    }, []);
    return (
        <div
            ref={ref}
            aria-hidden="true"
            className={`h-px origin-left ${strong ? "bg-ink/70 dark:bg-paper/60" : "bg-ink/15 dark:bg-paper/15"} ${className}`}
        />
    );
}

// Page-level opening: section mark, large serif title, intro. Animates on mount.
export function PageOpening({ section, label, title, intro, children }) {
    const titleRef = useRef(null);
    const restRef = useRef(null);
    useTypeset(titleRef, { onMount: true });
    useLayoutEffect(() => {
        if (prefersReducedMotion() || !restRef.current) return;
        const tween = gsap.from(restRef.current.children, {
            autoAlpha: 0,
            y: 14,
            duration: 1,
            ease: EASE,
            stagger: 0.08,
            delay: 0.45,
        });
        return () => tween.revert();
    }, []);

    return (
        <header className="pt-12 pb-14 sm:pt-16 sm:pb-20">
            <p className="mb-8 font-plex-mono text-[12px] uppercase tracking-[0.2em] text-stamp">
                {section} {label && <>&nbsp;{label}</>}
            </p>
            <h1
                ref={titleRef}
                className="max-w-5xl font-news text-[44px] font-normal leading-[1.02] tracking-[-0.02em] [text-wrap:balance] sm:text-[60px] lg:text-[76px]"
            >
                {title}
            </h1>
            <div ref={restRef}>
                {intro && (
                    <p className="mt-8 max-w-2xl font-plex text-[18px] leading-relaxed text-ink/70 dark:text-paper/70">
                        {intro}
                    </p>
                )}
                {children && <div className="mt-10">{children}</div>}
            </div>
        </header>
    );
}

// Numbered section with a margin column on the left (desktop), like an
// annotated report. `aside` renders under the label in the margin.
export function Section({ number, label, aside, children, className = "", id }) {
    return (
        <section id={id} className={`relative py-14 sm:py-20 ${className}`}>
            <Rule className="absolute inset-x-0 top-0" />
            <div className="grid gap-8 lg:grid-cols-12 lg:gap-10">
                <div className="lg:col-span-3">
                    <div className="lg:sticky lg:top-10">
                        {(number || label) && (
                            <p className="font-plex-mono text-[11.5px] uppercase tracking-[0.2em] text-ink/55 dark:text-paper/55">
                                {number && <span className="text-stamp">§ {number}</span>}
                                {number && label && <>&nbsp;&nbsp;</>}
                                {label}
                            </p>
                        )}
                        {aside && <div className="mt-6 hidden lg:block">{aside}</div>}
                    </div>
                </div>
                <div className="lg:col-span-9">{children}</div>
            </div>
        </section>
    );
}

// Heading inside a Section; typesets on scroll.
export function SectionTitle({ children, className = "" }) {
    const ref = useRef(null);
    useTypeset(ref);
    return (
        <h2
            ref={ref}
            className={`max-w-4xl font-news text-[34px] font-normal leading-[1.08] tracking-[-0.015em] [text-wrap:balance] sm:text-[44px] ${className}`}
        >
            {children}
        </h2>
    );
}

export function MarginNote({ children }) {
    return (
        <p className="border-l border-stamp pl-3 font-plex-mono text-[11.5px] leading-[1.6] text-ink/60 dark:text-paper/60">
            {children}
        </p>
    );
}

export function Prose({ children, className = "" }) {
    return (
        <div className={`max-w-2xl space-y-5 font-plex text-[17px] leading-[1.75] text-ink/75 dark:text-paper/75 ${className}`}>
            {children}
        </div>
    );
}

// Mono ledger table: rows of label / value, ruled.
export function Ledger({ rows, className = "" }) {
    return (
        <dl className={className}>
            {rows.map(([term, detail], i) => (
                <div
                    key={i}
                    className="grid grid-cols-1 gap-1 border-t border-ink/15 py-4 sm:grid-cols-[200px_1fr] sm:gap-6 dark:border-paper/15"
                >
                    <dt className="font-plex-mono text-[11.5px] uppercase tracking-[0.16em] leading-[26px] text-ink/50 dark:text-paper/50">{term}</dt>
                    <dd className="font-plex text-[16px] leading-relaxed text-ink/85 dark:text-paper/85">{detail}</dd>
                </div>
            ))}
        </dl>
    );
}

// Refresh ScrollTrigger once fonts land so triggers measure real layout.
export function useFontsRefresh() {
    useLayoutEffect(() => {
        document.fonts?.ready.then(() => ScrollTrigger.refresh());
    }, []);
}
